import { useState } from 'react'
import { Trans, useTranslation } from 'next-i18next'
import {
  Box,
  Button,
  ButtonLink,
  Stack,
  Text,
  TextLink,
  useToast
} from '@machobear/design-system'
import { Form, Formik } from 'formik'
import * as Yup from 'yup'

import { FieldInput } from '@/components/ds-form'

import { ReactComponent as SuccessCheckmark } from './success-checkmark.svg'

type Props = {
  zendeskSubject: string
  zendeskSubmitEvent: string
  zendeskClickId: string
  bookADemoClickId: string
}

const ZendeskForm = ({
  zendeskSubject,
  zendeskSubmitEvent,
  zendeskClickId,
  bookADemoClickId
}: Props) => {
  const [displayForm, setDisplayForm] = useState(true)
  const [user, setUser] = useState<null | {
    email: string
    firstName: string
    lastName: string
  }>(null)

  const { t } = useTranslation()
  const showToast = useToast()
  const onSubmit = async (values: any, { setSubmitting }: any) => {
    setSubmitting(true)
    const trimmedFullName = values.full_name?.trim()
    const formattedValues = {
      full_name: trimmedFullName,
      email: values.email,
      subject: zendeskSubject
    }
    const response = await fetch('/api/zendesk-ticket', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(formattedValues)
    })

    if (response.ok) {
      showToast({
        message: t(
          'b.halifax.form.toast_title',
          'Your form has been successfully submitted!'
        ),
        description: t(
          'b.halifax.form.react_out',
          `Someone from our team will reach out shortly`
        ),
        tone: 'positive'
      })
      window.dataLayer.push({
        event: 'form_submit',
        clickId: zendeskSubmitEvent
      })
      const splitName = trimmedFullName.split(' ')
      const firstName = splitName[0]
      const lastName = splitName[1] ?? ''
      setUser({
        email: values.email,
        firstName,
        lastName
      })
      setDisplayForm(false)
    } else {
      const error = await response.json()
      showToast({
        message: error.message,
        tone: 'critical'
      })
    }
  }

  const validationSchema = Yup.object().shape({
    full_name: Yup.string()
      .required('Full name is required')
      .matches(/^[aA-zZ\s]+$/, 'Can only contain letters'),
    email: Yup.string().email().required('Email is required')
  })

  const initialValues = {
    full_name: '',
    email: ''
  }
  const validate = (values: { full_name: string; email: string }) => {
    const errors: { full_name?: string } = {}

    const trimmedFullName = values.full_name.trim()

    if (trimmedFullName === '') {
      errors.full_name = 'Full name cannot be empty'
    }

    return errors
  }

  return displayForm ? (
    <Formik
      onSubmit={onSubmit}
      initialValues={initialValues}
      validationSchema={validationSchema}
      validate={validate}
    >
      {({ isSubmitting, handleSubmit }) => {
        return (
          <Form>
            <Stack space="gutter">
              <Stack space="small">
                <FieldInput
                  name="full_name"
                  label={t('b.checkout.field.full_name', 'Full Name')}
                  required
                />

                <FieldInput
                  name="email"
                  autoComplete="section-contact-form username email"
                  type="email"
                  label={t('common.work-email', 'Work Email')}
                  required
                />
              </Stack>
              <Box display="flex" alignItems="center">
                <Button
                  type="submit"
                  onClick={e => {
                    handleSubmit(e as any)
                  }}
                  disabled={isSubmitting}
                  size="large"
                  id={zendeskClickId}
                >
                  {isSubmitting
                    ? t('common.loading', 'Loading...')
                    : t('common.claim_discount', 'Claim Discount')}
                </Button>
              </Box>
              <Box>
                <Text size="xsmall" tone="tertiary">
                  <Trans t={t} i18nKey="b.auth.edit.signup_terms_halfiax.text">
                    By clicking “Submit”, I confirm that I have read and agree
                    to liv.rent{`'`}s{' '}
                    <TextLink
                      href="/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Terms & Conditions
                    </TextLink>{' '}
                    and{' '}
                    <TextLink
                      href="/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Privacy Policy
                    </TextLink>
                    .
                  </Trans>
                </Text>
              </Box>
            </Stack>
          </Form>
        )
      }}
    </Formik>
  ) : (
    <Stack space="xxlarge" align="center">
      <SuccessCheckmark originalSize />
      <Stack space="gutter" align="center">
        <Text size="xlarge" weight="strong" align="center">
          {t(
            'b.halfiax.success.title',
            'Your form has been successfully submitted!'
          )}
        </Text>
        <Text align="center">
          {t(
            'b.halfiax.success.description',
            'Our team will reach out within 1-3 days. For now, book a demo to see how liv.rent can streamline your rental journey.'
          )}
        </Text>
      </Stack>
      <ButtonLink
        variant="ghost"
        href={`https://calendly.com/livrent/30min-educational-session?email=${user?.email}&first_name=${user?.firstName}&last_name=${user?.lastName}`}
        target="_blank"
        id={bookADemoClickId}
      >
        {t('b.halfiax.success.book_a_demo', 'Book a demo')}
      </ButtonLink>
    </Stack>
  )
}

export default ZendeskForm
